import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {ChevronGlyph} from '@/components/ui/icons'
import {PageHeading} from '@/components/page-heading'
import {AttentionWorklist} from '@/components/attention-worklist'
import {useAdminStats} from '@/hooks/inci/consola/use-admin'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'

function StatCard({
  label,
  value,
  sub,
  onClick,
  testId,
}: {
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  onClick?: () => void
  testId?: string
}) {
  const content = (
    <>
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{label}</p>
      <p className="mt-2 font-heading text-[28px] leading-none text-foreground">{value}</p>
      {sub && <p className="mt-2 truncate text-xs text-muted-foreground">{sub}</p>}
    </>
  )
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="surface surface-raised group relative p-5 text-left">
        <span
          aria-hidden="true"
          className="absolute top-5 right-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary">
          <ChevronGlyph size={14} className="-rotate-90" />
        </span>
        {content}
      </button>
    )
  }
  return (
    <div className="surface p-5" data-testid={testId}>
      {content}
    </div>
  )
}

export function AdminAtencionPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const isSystemUser = useIsSystemUser()
  const {data: stats} = useAdminStats(isSystemUser)

  return (
    <div className="space-y-5" data-testid="admin-atencion-page">
      <PageHeading title={t('admin.atencion.title')} />

      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="admin-stats">
          <StatCard label={t('admin.stats.businesses')} value={stats.businesses} />
          <StatCard label={t('admin.stats.activeEmployees')} value={stats.active_employees} />
          <StatCard
            label={t('admin.stats.openAssessments')}
            value={stats.open_assessments}
            sub={t('admin.stats.bands', {
              critico: stats.open_bands.critico,
              moderado: stats.open_bands.moderadamente_aceptable,
              aceptable: stats.open_bands.aceptable,
            })}
            testId="stat-open-assessments"
            onClick={() => navigate('/admin/empresas?filtro=evaluacion_en_curso')}
          />
          <StatCard
            label={t('admin.stats.attention')}
            value={stats.needs_attention}
            sub={t('admin.stats.attentionDetail', {
              risk: stats.businesses_without_risk,
              assessment: stats.businesses_without_assessment,
            })}
            testId="stat-needs-attention"
            onClick={() => navigate('/admin/empresas?filtro=requiere_atencion')}
          />
        </div>
      )}

      <AttentionWorklist onOpen={(id) => navigate(`/admin/empresas/${id}`)} />
    </div>
  )
}
